import React, { useState } from 'react';
import { Percent, ArrowRight, Calculator, CheckCircle2, HelpCircle, ArrowLeft } from 'lucide-react';
import { AppView } from '../../types';

interface PercentageDiscountCalculatorProps {
  onNavigateToView: (view: AppView) => void;
  defaultTab?: 'percentage' | 'discount' | 'margin';
}

export const PercentageDiscountCalculator: React.FC<PercentageDiscountCalculatorProps> = ({ onNavigateToView, defaultTab = 'percentage' }) => {
  const [subTab, setSubTab] = useState<'percentage' | 'discount' | 'margin'>(defaultTab);

  // Percentage State
  const [pctType, setPctType] = useState<'of' | 'what-pct' | 'increase' | 'decrease'>('of');
  const [numX, setNumX] = useState<string>('20');
  const [numY, setNumY] = useState<string>('150');

  // Discount State
  const [originalPrice, setOriginalPrice] = useState<string>('150');
  const [discountPct, setDiscountPct] = useState<string>('20');
  const [secondDiscountPct, setSecondDiscountPct] = useState<string>('10'); // for multiple discounts

  // Margin / Markup State
  const [costPrice, setCostPrice] = useState<string>('70');
  const [sellPrice, setSellPrice] = useState<string>('100');

  // Calculations
  // 1. Percentage
  let pctResult = 0;
  let pctExplanation = '';
  const x = parseFloat(numX) || 0;
  const y = parseFloat(numY) || 0;

  if (pctType === 'of') {
    pctResult = (x * y) / 100;
    pctExplanation = `${x} % de ${y} = (${x} / 100) × ${y} = ${pctResult.toFixed(2)}`;
  } else if (pctType === 'what-pct') {
    pctResult = y !== 0 ? (x / y) * 100 : 0;
    pctExplanation = `${x} représente ${pctResult.toFixed(2)} % de ${y} (${x} / ${y} × 100)`;
  } else if (pctType === 'increase') {
    pctResult = x * (1 + y / 100);
    pctExplanation = `Augmentation de ${y}% sur ${x} = ${x} × (1 + ${y}/100) = ${pctResult.toFixed(2)}`;
  } else if (pctType === 'decrease') {
    pctResult = x * (1 - y / 100);
    pctExplanation = `Diminution de ${y}% sur ${x} = ${x} × (1 - ${y}/100) = ${pctResult.toFixed(2)}`;
  }

  // 2. Discount
  const orig = parseFloat(originalPrice) || 0;
  const d1 = parseFloat(discountPct) || 0;
  const d2 = parseFloat(secondDiscountPct) || 0;

  const priceAfterFirst = orig * (1 - d1 / 100);
  const finalDiscountedPrice = priceAfterFirst * (1 - d2 / 100);
  const totalSavings = orig - finalDiscountedPrice;
  const effectiveDiscountPct = orig > 0 ? (totalSavings / orig) * 100 : 0;

  // 3. Margin & Markup
  const cost = parseFloat(costPrice) || 0;
  const sell = parseFloat(sellPrice) || 0;
  const grossProfit = sell - cost;
  const marginPct = sell > 0 ? (grossProfit / sell) * 100 : 0;
  const markupPct = cost > 0 ? (grossProfit / cost) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      {/* Header */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('everyday-command-center')}
              className="p-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-[#0F172A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                YamooPay Everyday Money Engine (Prompt 14)
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">
                Calculateur de Pourcentages, Réductions & Marges
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            Centre de Commande
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        {/* Sub-tabs */}
        <div className="flex border-b border-[#E2E8F0] gap-6 mb-8">
          {[
            { id: 'percentage', label: 'Moteur de Pourcentage' },
            { id: 'discount', label: 'Calculateur de Réduction' },
            { id: 'margin', label: 'Marge & Surcote (Margin vs Markup)' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id as any)}
              className={`pb-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                subTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab 1: Percentage */}
        {subTab === 'percentage' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Paramètres du Pourcentage</h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
                {[
                  { id: 'of', label: 'X % de Y' },
                  { id: 'what-pct', label: 'X sur Y (%)' },
                  { id: 'increase', label: '+ Augmentation' },
                  { id: 'decrease', label: '- Diminution' },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setPctType(t.id as any)}
                    className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                      pctType === t.id
                        ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                        : 'bg-[#F8FAFC] text-[#475569] border-[#CBD5E1] hover:bg-[#F1F5F9]'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">
                    {pctType === 'of' ? 'Pourcentage (%)' : pctType === 'what-pct' ? 'Valeur X' : 'Valeur Initiale X'}
                  </label>
                  <input
                    type="number"
                    value={numX}
                    onChange={(e) => setNumX(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">
                    {pctType === 'of' ? 'Valeur de base Y' : pctType === 'what-pct' ? 'Valeur Totale Y' : 'Pourcentage de Variation (%)'}
                  </label>
                  <input
                    type="number"
                    value={numY}
                    onChange={(e) => setNumY(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Formule Déterministe</div>
                <div className="text-xs font-mono text-[#0F172A]">{pctExplanation}</div>
              </div>
            </div>

            {/* Results Card */}
            <div className="bg-gradient-to-br from-blue-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-2">Résultat Principal</div>
                <div className="text-4xl font-extrabold tracking-tight mt-1">
                  {pctResult.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-blue-200 mt-2">Calculateur de pourcentage certifié (Précision financière)</div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 text-xs text-slate-300">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Zéro division par zéro</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Traçabilité complète</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Discount */}
        {subTab === 'discount' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Calculateur de Réduction & Soldes</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Prix d'Origine</label>
                  <input
                    type="number"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Première Remise (%)</label>
                  <input
                    type="number"
                    value={discountPct}
                    onChange={(e) => setDiscountPct(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Seconde Remise (%) opt.</label>
                  <input
                    type="number"
                    value={secondDiscountPct}
                    onChange={(e) => setSecondDiscountPct(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
                <strong>Note sur les remises cumulées :</strong> 20% puis 10% de remise séquentielle équivaut à une remise effective de {effectiveDiscountPct.toFixed(2)}% (et non pas 30%).
              </div>
            </div>

            {/* Results Card */}
            <div className="bg-gradient-to-br from-emerald-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-emerald-300 uppercase tracking-wider mb-2">Prix Final</div>
                <div className="text-4xl font-extrabold tracking-tight mt-1">
                  ${finalDiscountedPrice.toFixed(2)}
                </div>
                <div className="mt-4 space-y-1 text-xs text-emerald-100">
                  <div>Économies totales : <strong className="text-white">${totalSavings.toFixed(2)}</strong></div>
                  <div>Remise effective : <strong className="text-white">{effectiveDiscountPct.toFixed(1)}%</strong></div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 text-xs text-slate-300">
                Application séquentielle rigoureuse garantie.
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Margin vs Markup */}
        {subTab === 'margin' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Marge vs Surcote (Margin vs Markup)</h3>
              <p className="text-xs text-[#475569] mb-6">
                <strong>Règle absolue :</strong> La marge est calculée par rapport au prix de vente, tandis que la surcote (markup) est calculée par rapport au coût. Ne jamais les confondre.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Coût de revient ($)</label>
                  <input
                    type="number"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Prix de Vente ($)</label>
                  <input
                    type="number"
                    value={sellPrice}
                    onChange={(e) => setSellPrice(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                  <div className="text-xs font-semibold text-[#64748B]">Bénéfice Brut</div>
                  <div className="text-xl font-bold text-[#0F172A] mt-1">${grossProfit.toFixed(2)}</div>
                </div>
                <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                  <div className="text-xs font-semibold text-[#64748B]">Taux de Marge (Margin)</div>
                  <div className="text-xl font-bold text-blue-600 mt-1">{marginPct.toFixed(2)} %</div>
                </div>
              </div>
            </div>

            {/* Results Card */}
            <div className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-indigo-300 uppercase tracking-wider mb-2">Taux de Surcote (Markup)</div>
                <div className="text-4xl font-extrabold tracking-tight mt-1">
                  {markupPct.toFixed(2)} %
                </div>
                <div className="text-xs text-indigo-200 mt-2">Markup = (Profit / Coût) × 100</div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 text-xs text-slate-300">
                Dissociation absolue garantie entre Marge et Surcote.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
